/**
 * **The "fiction-only in this client" predicate** — the one spelling every browser surface
 * uses to decide whether an entity carries the chip, so the pilot sheet, the mech sheet and
 * the deploy picker can never disagree.
 *
 * **Derived, never stored, never on the wire.** There is no `PilotData` field, no
 * `CombatantBase` field, no wire message and no migration behind this: every surface computes
 * it from the `(compendium, overlay)` pair `useCompendiumContext()` already hands it — the
 * same posture as `loadoutViolations`.
 *
 * **The loading suppression is the whole reason this is a hook and not a bare function.**
 * `CompendiumState.overlay` starts as the bare `SEED_OVERLAY` and is replaced only once the
 * sidecar fetch settles; a fetch failure degrades to that same bare overlay rather than
 * throwing. Classified against it, essentially the entire corpus reads `unmechanized` — so a
 * surface that rendered chips during the load would paint "fiction-only" across a sheet of
 * perfectly authored content. So **while `loadingOverlay` is true this predicate answers
 * `false` for everything** and no chip renders anywhere.
 *
 * **Scope line, inherited verbatim from `mechanization.ts` and NOT widened here.** The absence
 * of a chip means "reaches combat through a named vector or a non-empty authored overlay
 * entry" — it does **not** mean "a player can trigger this from the browser today". An entity
 * whose clause is authored but inert for an unrelated wiring reason (`ms_stable_structure`,
 * `ms_custom_paint_job`) shows no chip, because its gap is a wiring defect with its own owner
 * rather than an authoring gap. Don't let a surface's copy imply otherwise.
 */
import { useMemo } from "react";
import { classifyMechanization } from "@lancer-vtt/rules";
import { useCompendiumContext } from "./CompendiumProvider";

/** The chip's copy, in one place so the three surfaces cannot drift apart. Kept short — the full reason belongs in the row's `title`. */
export const FICTION_ONLY_LABEL = "fiction-only";

/** The `title`/tooltip text that explains the chip. Deliberately says "in this client", never "not in the rules". */
export const FICTION_ONLY_TITLE =
  "Fiction-only in this client: this content has rules text, but nothing in the app implements it. It still exists at the table — the GM adjudicates it.";

/**
 * A predicate over compendium entity ids — `true` only for an entity the mechanization
 * classifier reports as an outright gap (`unmechanized`).
 *
 * Note which verdicts are deliberately `false`: `ruled-narrative` (a human read the RAW text and
 * ruled it non-combat, so flagging it would be crying wolf about content that is *correctly*
 * doing nothing) and `nothing-to-mechanize` (RAW confers no mechanical content at all). Only the
 * honest gap earns a chip.
 */
export type FictionOnlyPredicate = (entryId: string) => boolean;

/**
 * The hook every chip-rendering surface calls. Returns a stable predicate that is a constant
 * `false` while the overlay is still loading (see the loading-suppression note above).
 *
 * Talent ranks are addressed by their composite `"<talentId>:<rank>"` id, exactly as the
 * classifier and `roster-view.ts` address them — `t_black_thumb:1` can differ from `:2`, so a
 * surface must never pass the bare talent id.
 */
export function useFictionOnly(): FictionOnlyPredicate {
  const { compendium, overlay, loadingOverlay } = useCompendiumContext();
  return useMemo<FictionOnlyPredicate>(() => {
    if (loadingOverlay) return () => false;
    return (entryId: string) => classifyMechanization({ entryId, compendium, overlay }) === "unmechanized";
  }, [compendium, overlay, loadingOverlay]);
}
